import { Component, ChangeDetectionStrategy, ViewChild, ElementRef, ChangeDetectorRef, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-new-project-gallery',
  templateUrl: './new-project-gallery.component.html',
  styleUrls: ['./new-project-gallery.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DragDropModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class NewProjectGalleryComponent implements OnInit, OnChanges {
  @Input() galleryFormArray!: FormArray | null;
  @Input() projectId!: string | null; // ID del proyecto
  @Input() propertyId!: string | null; // ID de la propiedad
  @Input() isMainProperty: boolean = false; // Indica si esta propiedad es la de portada
  @Input() propertyCoverIndex: number = 0; // Índice de la imagen de portada para ESTA propiedad
  @Input() mainPropertyCoverIndex: number = 0; // Índice de la imagen de portada principal
  @Output() imageFileSelected = new EventEmitter<{ file: File; index: number; propertyId: string }>();
  @Output() imageRemoved = new EventEmitter<{ index: number; propertyId: string }>();
  @Output() setCoverIndex = new EventEmitter<number>();
  @Output() imagesReordered = new EventEmitter<{ previousIndex: number; currentIndex: number; propertyId: string }>();
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  
  selectedImageIndex: number = 0;
  private _hasMultipleImages: boolean = false;
  private fileInputElement: HTMLInputElement | null = null;
  
  // Array fijo de 5 placeholders - ÚNICA FUENTE DE VERDAD
  thumbnails: Array<{image: string | null}> = [
    { image: null },
    { image: null },
    { image: null },
    { image: null },
    { image: null }
  ];

  constructor(private cdr: ChangeDetectorRef) {
    // Crear input reutilizable una sola vez
    this.initializeFileInput();
  }

  ngOnInit(): void {
    this.loadImagesFromFormArray();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['galleryFormArray']) {
      this.loadImagesFromFormArray();
      // Suscribirse a cambios en el FormArray para actualizar previews cuando se reciban URLs
      if (this.galleryFormArray) {
        this.syncThumbnailsFromFormArray();
      }
    }
    // Si cambia el projectId o propertyId, también recargar (por si acaso)
    if (changes['projectId'] || changes['propertyId']) {
      this.loadImagesFromFormArray();
    }
    
    // Forzar actualización de change detection si cambian propiedades que afectan la vista directamente
    if (changes['isMainProperty'] || changes['mainPropertyCoverIndex']) {
      this.cdr.markForCheck();
    }
  }

  private syncThumbnailsFromFormArray(): void {
    if (!this.galleryFormArray) return;
    
    // Actualizar thumbnails si el FormArray tiene URLs que aún no están en los thumbnails
    this.galleryFormArray.controls.forEach((control, index) => {
      const url = control.value;
      if (url && url.startsWith('http') && (!this.thumbnails[index]?.image || !this.thumbnails[index]?.image?.startsWith('http'))) {
        // Si es una URL de Firebase y el thumbnail actual no es una URL, actualizar
        if (this.thumbnails[index]) {
          this.thumbnails[index].image = url;
        }
      }
    });
    
    this.cdr.markForCheck();
  }

  private loadImagesFromFormArray(): void {
    if (!this.galleryFormArray) return;
    
    const images = this.galleryFormArray.value || [];
    console.log(`[Gallery] loadImagesFromFormArray called for propertyId=${this.propertyId}. FormArray value:`, images);
    
    // Limpiar thumbnails existentes
    this.thumbnails = [
      { image: null },
      { image: null },
      { image: null },
      { image: null },
      { image: null }
    ];
    
    // Cargar imágenes existentes (pueden ser URLs o base64 temporales)
    images.forEach((imageUrl: string, index: number) => {
      if (index < this.thumbnails.length && imageUrl && imageUrl.trim() !== '') {
        this.thumbnails[index].image = imageUrl;
      }
    });
    
    // Seleccionar la primera imagen disponible
    const firstImageIndex = this.thumbnails.findIndex(thumb => thumb.image);
    if (firstImageIndex !== -1) {
      this.selectedImageIndex = firstImageIndex;
    }
    
    this.updateHasMultipleImages();
    console.log(`[Gallery] Thumbnails after load:`, this.thumbnails.map(t => t.image ? 'has image' : 'null'));
    this.cdr.markForCheck();
  }

  private initializeFileInput(): void {
    this.fileInputElement = document.createElement('input');
    this.fileInputElement.type = 'file';
    this.fileInputElement.accept = 'image/*';
    this.fileInputElement.multiple = false;
    this.fileInputElement.style.display = 'none';
    document.body.appendChild(this.fileInputElement);
  }

  addImage(): void {
    if (!this.fileInputElement) return;
    
    this.fileInputElement.onchange = (event: any) => {
      this.handleFileSelection(event, -1); // -1 = buscar primer vacío
    };
    
    this.fileInputElement.click();
  }

  selectThumbnail(index: number): void {
    if (this.thumbnails[index].image) {
      // Si tiene imagen, seleccionarla
      this.selectedImageIndex = index;
    } else {
      // Si está vacío, añadir imagen directamente
      this.addImageToThumbnail(index);
    }
  }

  changeThumbnailImage(index: number, event: Event): void {
    event.stopPropagation();
    this.addImageToThumbnail(index);
  }

  removeImage(index: number, event: Event): void {
    event.stopPropagation();
    this.thumbnails[index].image = null;
    this.updateHasMultipleImages();
    this.updateFormArray();
    this.cdr.markForCheck();
    if (this.propertyId) {
      this.imageRemoved.emit({index, propertyId: this.propertyId});
    }
  }

  setAsCover(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.setCoverIndex.emit(this.selectedImageIndex);
  }

  drop(event: CdkDragDrop<Array<{image: string | null}>>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    if (this.selectedImageIndex === event.previousIndex) {
      this.selectedImageIndex = event.currentIndex;
    } else if (
      this.selectedImageIndex > event.previousIndex &&
      this.selectedImageIndex <= event.currentIndex
    ) {
      this.selectedImageIndex--;
    } else if (
      this.selectedImageIndex < event.previousIndex &&
      this.selectedImageIndex >= event.currentIndex
    ) {
      this.selectedImageIndex++;
    }

    moveItemInArray(this.thumbnails, event.previousIndex, event.currentIndex);

    if (this.mainPropertyCoverIndex === event.previousIndex) {
      this.setCoverIndex.emit(event.currentIndex);
    } else if (
      this.mainPropertyCoverIndex > event.previousIndex &&
      this.mainPropertyCoverIndex <= event.currentIndex
    ) {
      this.setCoverIndex.emit(this.mainPropertyCoverIndex - 1);
    } else if (
      this.mainPropertyCoverIndex < event.previousIndex &&
      this.mainPropertyCoverIndex >= event.currentIndex
    ) {
      this.setCoverIndex.emit(this.mainPropertyCoverIndex + 1);
    }

    this.syncFormArrayFromThumbnails();
    
    if (this.propertyId) {
      this.imagesReordered.emit({
        previousIndex: event.previousIndex,
        currentIndex: event.currentIndex,
        propertyId: this.propertyId
      });
    }
  }

  private syncFormArrayFromThumbnails(): void {
    const formArray = this.galleryFormArray;
    if (!formArray) return;

    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }

    this.thumbnails.forEach(thumb => {
      formArray.push(new FormControl(thumb.image || ''));
    });

    this.cdr.markForCheck();
  }


  addImageToThumbnail(index: number): void {
    if (!this.fileInputElement) return;
    
    this.fileInputElement.onchange = (event: any) => {
      this.handleFileSelection(event, index);
    };
    
    this.fileInputElement.click();
  }

  selectImage(index: number): void {
    if (index >= 0 && index < this.thumbnails.length && this.thumbnails[index].image) {
      this.selectedImageIndex = index;
    }
  }

  nextImage(): void {
    // Buscar la siguiente imagen disponible
    let nextIndex = this.selectedImageIndex;
    do {
      nextIndex = (nextIndex + 1) % this.thumbnails.length;
    } while (nextIndex !== this.selectedImageIndex && !this.thumbnails[nextIndex].image);
    
    if (this.thumbnails[nextIndex].image) {
      this.selectedImageIndex = nextIndex;
    }
  }

  previousImage(): void {
    // Buscar la imagen anterior disponible
    let prevIndex = this.selectedImageIndex;
    do {
      prevIndex = (prevIndex - 1 + this.thumbnails.length) % this.thumbnails.length;
    } while (prevIndex !== this.selectedImageIndex && !this.thumbnails[prevIndex].image);
    
    if (this.thumbnails[prevIndex].image) {
      this.selectedImageIndex = prevIndex;
    }
  }


  trackByIndex(index: number, item: any): number {
    return index;
  }

  get hasMultipleImages(): boolean {
    return this._hasMultipleImages;
  }

  private updateHasMultipleImages(): void {
    const imageCount = this.thumbnails.filter(t => t.image).length;
    this._hasMultipleImages = imageCount > 1;
  }

  handleMainImageClick(): void {
    if (this.thumbnails[this.selectedImageIndex]?.image) {
      this.changeImage();
    } else {
      this.addImageToThumbnail(this.selectedImageIndex);
    }
  }

  changeImage(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    if (!this.fileInputElement) return;
    
    this.fileInputElement.onchange = (event: any) => {
      this.handleFileSelection(event, this.selectedImageIndex);
    };
    
    this.fileInputElement.click();
  }

  private handleFileSelection(event: any, targetIndex: number): void {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido');
      return;
    }
    
    // Validar tamaño (opcional: 5MB máximo)
    // if (file.size > 5 * 1024 * 1024) {
    //   alert('La imagen es demasiado grande. Máximo 5MB');
    //   return;
    // }
    
    this.processImageFile(file, targetIndex);
  }

  private processImageFile(file: File, targetIndex: number): void {
    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido');
      return;
    }
    
    // Validar tamaño (opcional: 5MB máximo)
    // if (file.size > 5 * 1024 * 1024) {
    //   alert('La imagen es demasiado grande. Máximo 5MB');
    //   return;
    // }
    
    // Crear preview local mientras se sube
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const imageData = e.target.result;
      
      let finalIndex = -1;
      
      if (targetIndex === -1) {
        // Buscar primer placeholder vacío
        const emptyIndex = this.thumbnails.findIndex(thumb => !thumb.image);
        if (emptyIndex !== -1) {
          this.thumbnails[emptyIndex].image = imageData; // Preview temporal
          this.selectedImageIndex = emptyIndex;
          finalIndex = emptyIndex;
        }
      } else {
        // Reemplazar imagen específica
        this.thumbnails[targetIndex].image = imageData; // Preview temporal
        this.selectedImageIndex = targetIndex;
        finalIndex = targetIndex;
      }
      
      if (finalIndex === -1) return; // No hay espacio
      
      this.updateHasMultipleImages();
      this.updateFormArray(); // Persistir preview sincrónicamente para evitar pérdida al cambiar de tab
      this.cdr.markForCheck();
      
      // Emitir evento para que el componente padre suba el archivo a Storage
      if (this.propertyId) {
        this.imageFileSelected.emit({
          file: file,
          index: finalIndex,
          propertyId: this.propertyId
        });
      }
    };
    
    reader.readAsDataURL(file);
  }

  private updateFormArray(): void {
    const formArray = this.galleryFormArray;
    if (!formArray) return;
    
    console.log(`[Gallery] updateFormArray called. Current FormArray value before update:`, formArray.value);
    
    // Sincronizar el FormArray con los thumbnails actuales
    // Mantener las URLs existentes si ya están guardadas (empiezan con http)
    // Solo actualizar si hay cambios en los thumbnails
    
    this.thumbnails.forEach((thumb, index) => {
      const currentValue = formArray.controls[index]?.value;
      
      // Si el thumbnail tiene una imagen nueva (base64) y el FormArray tiene una URL, no sobrescribir
      // Solo actualizar si hay una diferencia real
      if (thumb.image) {
        // Si el FormArray no tiene este índice o está vacío, agregar/actualizar
        if (!formArray.controls[index]) {
          formArray.push(new FormControl(thumb.image));
        } else if (!currentValue || currentValue !== thumb.image) {
          // Si el valor actual no es una URL (http/https), actualizar
          // O si es un base64 nuevo, actualizar (el componente padre actualizará con la URL después)
          if (!currentValue?.startsWith('http')) {
            (formArray.controls[index] as FormControl).setValue(thumb.image);
          }
        }
      } else {
        // Si el thumbnail está vacío pero el FormArray tiene valor, limpiarlo solo si no es una URL
        if (formArray.controls[index] && currentValue && !currentValue.startsWith('http')) {
          (formArray.controls[index] as FormControl).setValue('');
        }
      }
    });
    
    // Asegurar que hay exactamente 5 controles (o los necesarios)
    while (formArray.length < 5) {
      formArray.push(new FormControl(''));
    }
    console.log(`[Gallery] FormArray updated, final value:`, formArray.value);
  }
}
